import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Save, RotateCcw, Undo2, Redo2, Eye, EyeOff, Monitor, Smartphone, Tablet, History, Plus, Type, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { VisualEditor } from "./VisualEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardCls = "rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]";
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30";

const SECTION_META = {
  hero: { label: "Hero", texts: ["headline", "subtext", "button"] },
  tours: { label: "Tours", texts: ["headline", "subtext"] },
  services: { label: "Services", texts: ["headline", "subtext"] },
  destinations: { label: "Destinations", texts: ["headline", "subtext"] },
  about: { label: "About", texts: ["headline", "subtext"] },
  contact: { label: "Contact", texts: ["headline", "subtext"] },
  footer: { label: "Footer", texts: ["tagline"] },
};

const DEFAULT_SECTION = { hidden: false, padding: 0, bg_color: "", text_color: "", accent_color: "", texts: {}, images: {}, order: 0 };

export const WebsiteStudioTab = () => {
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [device, setDevice] = useState("desktop");
  const [mode, setMode] = useState("classic");
  const [selected, setSelected] = useState("hero");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const saveTimer = useRef(null);
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const firstRun = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-design/full`, { withCredentials: true });
      setDoc(data);
      setHistory(data.history || []);
      setDirty(!!data.draft);
    } catch {
      toast.error("Could not load website design.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sections = doc?.draft || doc?.sections || {};
  const sec = sections[selected] || DEFAULT_SECTION;

  const pushUndo = (prev) => { setUndoStack((s) => [...s.slice(-19), prev]); setRedoStack([]); };

  const update = (patch) => {
    pushUndo(sections);
    setDoc((d) => ({ ...d, draft: { ...sections, [selected]: { ...sec, ...patch } } }));
    setDirty(true);
  };

  // Auto-save draft
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (!dirty) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/site-design`, { sections }, { withCredentials: true });
      } catch { /* retry on next change */ }
    }, 1000);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, dirty]);

  const publish = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/site-design`, { sections }, { withCredentials: true });
      await axios.post(`${API}/site-design/publish`, {}, { withCredentials: true });
      toast.success("Published — your website design is live now.");
      setDirty(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed.");
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setRedoStack((s) => [...s, sections]);
    setDoc((d) => ({ ...d, draft: prev }));
    setDirty(true);
  };

  const redo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    setUndoStack((s) => [...s, sections]);
    setDoc((d) => ({ ...d, draft: next }));
    setDirty(true);
  };

  const restore = async (index) => {
    if (!window.confirm(index === null ? "Restore the original website design? Your custom changes will be replaced." : "Restore this previous version?")) return;
    try {
      const body = index === null ? {} : { index };
      const { data } = await axios.post(`${API}/site-design/restore`, body, { withCredentials: true });
      toast.success(index === null ? "Original design restored." : "Version restored.");
      setDoc(data);
      setDirty(true);
      setShowHistory(false);
    } catch {
      toast.error("Could not restore.");
    }
  };

  const bg = sec.background || {};
  const bgUrlKey = device === "mobile" ? "url_mobile" : device === "tablet" ? "url_tablet" : "url";
  const updateBg = (patch) => update({ background: { ...bg, ...patch } });
  const vid = sec.video || {};
  const updateVideo = (patch) => update({ video: { ...vid, ...patch } });
  const uploadBg = async (file) => {
    if (!file) return;
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/media`, data, { withCredentials: true });
      updateBg({ [bgUrlKey]: m.url, enabled: true });
      toast.success(`Background set for ${device}.`);
    } catch { toast.error("Upload failed."); }
  };
  const uploadVideo = async (file) => {
    if (!file) return;
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/media`, data, { withCredentials: true });
      updateVideo({ url: m.url, enabled: true });
      toast.success("Video player updated.");
    } catch { toast.error("Video upload failed."); }
  };

  if (!doc) return <p className="text-sm text-slate-400">Loading website design…</p>;

  if (mode === "visual") {
    return (
      <div className="space-y-4" data-testid="website-studio-tab">
        <div className="flex items-center gap-2">
          <button onClick={() => setMode("classic")} data-testid="wstudio-mode-classic" className="rounded-full px-4 py-2 text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:border-[var(--brand)]">Form editor</button>
          <button onClick={() => setMode("visual")} data-testid="wstudio-mode-visual" className="rounded-full px-4 py-2 text-xs font-bold bg-[var(--brand)] text-white shadow">Visual editor</button>
        </div>
        <VisualEditor />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="website-studio-tab">
      <div className="flex items-center gap-2" data-testid="wstudio-mode-switch">
        <button onClick={() => setMode("classic")} data-testid="wstudio-mode-classic" className="rounded-full px-4 py-2 text-xs font-bold bg-[#0b132b] text-white shadow">Form editor</button>
        <button onClick={() => setMode("visual")} data-testid="wstudio-mode-visual" className="rounded-full px-4 py-2 text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:border-[var(--brand)]">Visual editor</button>
      </div>
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg text-[#0b132b]">Website Studio</h2>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Change text, colors, images and section visibility on your real website. Everything auto-saves as a draft; Publish Now makes it live instantly. Undo / Redo / Restore keep you safe.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={undo} disabled={!undoStack.length} data-testid="wstudio-undo" className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40" aria-label="Undo"><Undo2 className="w-4 h-4" /></button>
            <button onClick={redo} disabled={!redoStack.length} data-testid="wstudio-redo" className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40" aria-label="Redo"><Redo2 className="w-4 h-4" /></button>
            <button onClick={() => setShowHistory((v) => !v)} data-testid="wstudio-history" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 text-slate-500 text-xs font-bold px-3 py-2.5 hover:bg-slate-50"><History className="w-4 h-4" /> Versions</button>
            <button onClick={() => restore(null)} data-testid="wstudio-restore-default" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 text-slate-500 text-xs font-bold px-3 py-2.5 hover:bg-slate-50"><RotateCcw className="w-4 h-4" /> Restore original</button>
            <button onClick={publish} disabled={busy} data-testid="wstudio-publish" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> {busy ? "Publishing…" : "Publish Now"}
            </button>
          </div>
        </div>
        <p className="mt-3 text-[11px] font-semibold" data-testid="wstudio-draft-status">
          {dirty ? <span className="text-amber-600">Unpublished draft — auto-saving</span> : <span className="text-emerald-600">All changes published</span>}
        </p>
      </div>

      {showHistory && (
        <div className={cardCls} data-testid="wstudio-history-panel">
          <h3 className="font-display font-bold text-sm text-[#0b132b] mb-3">Previous versions</h3>
          {history.length === 0 && <p className="text-xs text-slate-400">No previous versions yet — versions are created each time you publish.</p>}
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between rounded-2xl border border-slate-100 px-4 py-2.5">
                <span className="text-xs font-semibold text-slate-600">{h.label}</span>
                <button onClick={() => restore(i)} data-testid={`wstudio-restore-${i}`} className="text-[11px] font-bold text-[var(--brand)] hover:underline">Restore</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-[300px_1fr] gap-5 items-start">
        {/* section list + editor */}
        <div className="space-y-5">
          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-3">Sections</h3>
            <div className="space-y-1.5" data-testid="wstudio-sections">
              {Object.keys(SECTION_META).map((key) => {
                const s = sections[key] || DEFAULT_SECTION;
                return (
                  <button
                    key={key}
                    onClick={() => setSelected(key)}
                    data-testid={`wstudio-section-${key}`}
                    className={`w-full flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${selected === key ? "bg-[#0b132b] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    <span className="flex items-center gap-2">
                      {s.hidden ? <EyeOff className="w-3.5 h-3.5 opacity-60" /> : <Eye className="w-3.5 h-3.5 opacity-60" />}
                      {SECTION_META[key].label}
                    </span>
                    {s.hidden && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Hidden</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* selected section editor */}
          <div className={cardCls} data-testid="wstudio-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-sm text-[#0b132b]">{SECTION_META[selected].label}</h3>
              <button
                onClick={() => update({ hidden: !sec.hidden })}
                data-testid="wstudio-toggle-hidden"
                className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-3 py-1.5 ${sec.hidden ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
              >
                {sec.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {sec.hidden ? "Hidden" : "Visible"}
              </button>
            </div>
            <div className="space-y-4">
              {SECTION_META[selected].texts.map((key) => (
                <label key={key} className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1"><Type className="w-3 h-3" /> {key}</span>
                  <textarea
                    value={sec.texts?.[key]?.[device] || ""}
                    onChange={(e) => update({ texts: { ...sec.texts, [key]: { ...(sec.texts?.[key] || {}), [device]: e.target.value } } })}
                    rows={2}
                    className={`${inputCls} mt-1`}
                    placeholder={`${key} (${device})`}
                    data-testid={`wstudio-text-${key}`}
                  />
                </label>
              ))}
              <div className="rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="wstudio-bg-manager">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Background Picture Manager · <span className="text-[var(--brand)]">{device}</span></span>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                    <input type="checkbox" checked={bg.enabled !== false && !!(bg.url || bg.url_mobile || bg.url_tablet)} onChange={(e) => updateBg({ enabled: e.target.checked })} className="w-3.5 h-3.5 accent-[var(--brand)]" data-testid="wstudio-bg-enabled" /> On
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input value={bg[bgUrlKey] || ""} onChange={(e) => updateBg({ [bgUrlKey]: e.target.value })} className={`${inputCls} flex-1`} placeholder={`Image URL for ${device}`} data-testid="wstudio-bg-url" />
                  <button onClick={() => imgRef.current?.click()} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="wstudio-bg-upload"><Plus className="w-4 h-4" /></button>
                  <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} />
                </div>
                {(bg.url || bg.url_mobile || bg.url_tablet) && (
                  <img src={bg[bgUrlKey] || bg.url} alt="" className="w-full h-24 object-cover rounded-xl border border-slate-100" style={{ objectPosition: bg.position || "center" }} />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Focal point</span>
                    <select value={bg.position || "center"} onChange={(e) => updateBg({ position: e.target.value })} className={`${inputCls} mt-1`} data-testid="wstudio-bg-position">
                      {["center", "top", "bottom", "left", "right", "top left", "top right", "bottom left", "bottom right"].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Scroll</span>
                    <select value={bg.fixed ? "fixed" : "scroll"} onChange={(e) => updateBg({ fixed: e.target.value === "fixed" })} className={`${inputCls} mt-1`} data-testid="wstudio-bg-fixed">
                      <option value="scroll">Scrolls normally</option><option value="fixed">Fixed (parallax)</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Overlay colour</span>
                    <input type="color" value={bg.overlay_color || "#0b132b"} onChange={(e) => updateBg({ overlay_color: e.target.value })} className="mt-1 w-full h-9 rounded-lg border border-slate-200" data-testid="wstudio-bg-overlay-color" /></label>
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Overlay strength {Math.round((bg.overlay_opacity ?? 0) * 100)}%</span>
                    <input type="range" min={0} max={1} step={0.05} value={bg.overlay_opacity ?? 0} onChange={(e) => updateBg({ overlay_opacity: Number(e.target.value) })} className="mt-2 w-full accent-[var(--brand)]" data-testid="wstudio-bg-overlay-opacity" /></label>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Text scrim (white) {Math.round((bg.scrim ?? 0.85) * 100)}%</span>
                    <input type="range" min={0} max={1} step={0.05} value={bg.scrim ?? 0.85} onChange={(e) => updateBg({ scrim: Number(e.target.value) })} className="mt-2 w-full accent-[var(--brand)]" data-testid="wstudio-bg-scrim" /></label>
                  <label className="block"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Scrim side</span>
                    <select value={bg.scrim_direction || "left"} onChange={(e) => updateBg({ scrim_direction: e.target.value })} className={`${inputCls} mt-1`} data-testid="wstudio-bg-scrim-dir">
                      <option value="left">Left</option><option value="bottom">Bottom</option><option value="none">None</option>
                    </select>
                  </label>
                </div>
              </div>
              {selected === "hero" && (
                <div className="rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="wstudio-video-manager">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Hero Video Player</span>
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      <input type="checkbox" checked={vid.enabled !== false} onChange={(e) => updateVideo({ enabled: e.target.checked })} className="w-3.5 h-3.5 accent-[var(--brand)]" data-testid="wstudio-video-enabled" /> Show player
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-400 -mt-1">Plays in the player beside your text. The background stays a picture. Leave the URL empty to use your previous video.</p>
                  <div className="flex items-center gap-2">
                    <input value={vid.url || ""} onChange={(e) => updateVideo({ url: e.target.value })} className={`${inputCls} flex-1`} placeholder="Video URL (mp4/webm)" data-testid="wstudio-video-url" />
                    <button onClick={() => vidRef.current?.click()} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="wstudio-video-upload" aria-label="Upload video"><Plus className="w-4 h-4" /></button>
                    {vid.url && <button onClick={() => updateVideo({ url: "" })} className="p-2.5 rounded-xl border border-slate-200 text-slate-400 hover:text-[var(--brand)]" data-testid="wstudio-video-remove" aria-label="Remove video"><Trash2 className="w-4 h-4" /></button>}
                    <input ref={vidRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
                  </div>
                  {vid.url && <video src={vid.url} muted loop autoPlay playsInline className="w-full h-28 object-cover rounded-xl border border-slate-100" data-testid="wstudio-video-preview" />}
                  <button onClick={() => updateVideo({ url: "", enabled: true })} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold px-3 py-2 hover:bg-slate-50" data-testid="wstudio-video-restore"><RotateCcw className="w-3.5 h-3.5" /> Restore previous video</button>
                  <p className="text-[10px] text-slate-400">Size &amp; position of the player are set in the <span className="font-semibold">Visual editor</span> (select &ldquo;Hero video player&rdquo;).</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {[["bg_color", "Background"], ["text_color", "Text"], ["accent_color", "Accent"]].map(([k, label]) => (
                  <div key={k}>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
                    <input type="color" value={sec[k] || "#ffffff"} onChange={(e) => update({ [k]: e.target.value })} className="mt-1 w-full h-9 rounded-lg border border-slate-200 cursor-pointer" data-testid={`wstudio-color-${k}`} />
                  </div>
                ))}
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Spacing</span>
                <input type="range" min={-2} max={2} step={1} value={sec.padding} onChange={(e) => update({ padding: Number(e.target.value) })} className="mt-1 w-full accent-[var(--brand)]" data-testid="wstudio-padding" />
                <div className="flex justify-between text-[10px] text-slate-400"><span>Compact</span><span>Spacious</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* live preview */}
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center rounded-full border border-slate-200 p-0.5">
              {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
                <button key={d} onClick={() => setDevice(d)} data-testid={`wstudio-device-${d}`} className={`p-2 rounded-full transition-colors ${device === d ? "bg-[#0b132b] text-white" : "text-slate-400"}`} aria-label={d}>
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
            <span className="text-[10px] text-slate-300 font-semibold">Preview of your live homepage</span>
          </div>
          <div className={`mx-auto overflow-hidden rounded-2xl border border-slate-200 bg-white ${device === "mobile" ? "w-[320px]" : device === "tablet" ? "w-[640px] max-w-full" : "w-full"}`} style={{ height: 560 }}>
            <iframe
              key={`${device}-${doc.published_at || "x"}`}
              src={`/?studio_preview=1`}
              title="Website preview"
              className="w-full h-full border-0"
              data-testid="wstudio-iframe"
            />
          </div>
          <p className="mt-2 text-[10px] text-slate-400 text-center">Changes publish to the live site when you press Publish Now.</p>
        </div>
      </div>
    </div>
  );
};
